/**
 * GUMSHOE — composition root. Owned by the integrator; modules never edit.
 * Init order: render → world → lighting → player → game → audio → ui.
 */
import { DEFAULT_NOIR_RENDER_CONFIG } from './core/constants'
import { createDebugApi, installDebugApi } from './core/debug'
import { isTouchDevice } from './core/platform'
import { createEventBus } from './core/events'
import { createGameLoop } from './core/loop'
import type { ModuleContext } from './core/types'
import { initAudio } from './audio'
import { initGame } from './game'
import { initLighting } from './lighting'
import { initPlayer } from './player'
import { initRender } from './render'
import { initUi } from './ui'
import { initWorld } from './world'

const bootstrap = (): void => {
  const container = document.getElementById('app')
  if (!container) {
    throw new Error('GUMSHOE: #app container not found in index.html')
  }

  const bus = createEventBus()
  const loop = createGameLoop()
  const { registry: debug, api } = createDebugApi()
  installDebugApi(api)

  // Mobile GPUs pay full price for the HDR bloom chain — cap the DPR.
  const renderConfig = isTouchDevice()
    ? { ...DEFAULT_NOIR_RENDER_CONFIG, maxPixelRatio: 1.5 }
    : DEFAULT_NOIR_RENDER_CONFIG
  const handles = initRender(container, renderConfig)

  const ctx: ModuleContext = {
    scene: handles.scene,
    camera: handles.camera,
    bus,
    loop,
    debug
  }

  initWorld(ctx)
  initLighting(ctx)
  initPlayer(ctx)
  initGame(ctx)
  initAudio(ctx)
  initUi(ctx)

  // Indoor air is still: heavy exterior rain-haze flattened the office to
  // milk (QA rounds 1-3), so the fog density follows the player's zone.
  const FOG_DENSITY_OUTDOOR = DEFAULT_NOIR_RENDER_CONFIG.fogDensity
  const FOG_DENSITY_INDOOR = 0.005
  bus.on('zone:changed', ({ zone }) => {
    handles.setConfig({
      fogDensity: zone === 'indoor' ? FOG_DENSITY_INDOOR : FOG_DENSITY_OUTDOOR
    })
  })

  loop.onRender(() => handles.render())
  loop.start()
}

bootstrap()
